import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = `${API_BASE}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// ---------------------------------------------------------------------------
// Auto-refresh expired access tokens.
//
// Access tokens are short-lived (60 min). Previously nothing ever called
// POST /auth/refresh, so once a session crossed that 60-minute mark every
// subsequent request (e.g. submitting the "New Candidate" form after filling
// it out for a while) failed with a raw 401 "Token expired" error and no
// recovery. The refresh_token cookie (7-day expiry, httponly) was already
// being set at login and already had a working backend endpoint — it just
// wasn't being used. This interceptor transparently refreshes the access
// token and retries the failed request once, so the user never sees it.
// ---------------------------------------------------------------------------
let isRefreshingToken = false;
let pendingRequests = [];

function resolvePendingRequests(newToken) {
  pendingRequests.forEach((cb) => cb(newToken));
  pendingRequests = [];
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || '';
    const isAuthEndpoint = /\/auth\/(login|register|refresh)(\?|$)/.test(url);

    if (status !== 401 || isAuthEndpoint || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        pendingRequests.push((newToken) => {
          if (!newToken) { reject(error); return; }
          originalRequest._retry = true;
          originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
          resolve(axios(originalRequest));
        });
      });
    }

    originalRequest._retry = true;
    isRefreshingToken = true;
    try {
      const { data } = await axios.post(`${API}/auth/refresh`, {});
      const newToken = data?.token;
      if (newToken) localStorage.setItem('token', newToken);
      resolvePendingRequests(newToken);
      originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
      return axios(originalRequest);
    } catch (refreshError) {
      resolvePendingRequests(null);
      localStorage.removeItem('token');
      // Signed-out visitors must stay on the public sign-in and sign-up pages.
      if (typeof window !== 'undefined' && !['/login', '/register'].includes(window.location.pathname)) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshingToken = false;
    }
  }
);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const { data } = await axios.get(`${API}/auth/me`, config);
      setUser(data);
    } catch {
      setUser(false);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.token) localStorage.setItem('token', data.token);
    setUser(data);
    return data;
  };

  const register = async (formData) => {
    const { data } = await axios.post(`${API}/auth/register`, formData);
    if (data.pending_approval) return data;
    if (data.token) localStorage.setItem('token', data.token);
    setUser(data);
    return data;
  };

  const completeOnboarding = async () => {
    await apiPost('/auth/onboarding/complete', {});
    setUser(current => ({ ...current, onboarding_required: false }));
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    localStorage.removeItem('token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, completeOnboarding, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Axios helper with token
export function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function apiGet(path, config = {}) {
  // `config` is optional (e.g. { signal } to cancel a stale request) and
  // merges on top of the auth header — every existing call that only ever
  // passed `path` behaves exactly as before.
  return axios.get(`${API}${path}`, { headers: authHeaders(), ...config });
}

export function apiPost(path, data) {
  return axios.post(`${API}${path}`, data, { headers: authHeaders() });
}

export function apiPut(path, data) {
  return axios.put(`${API}${path}`, data, { headers: authHeaders() });
}

export function apiDelete(path) {
  return axios.delete(`${API}${path}`, { headers: authHeaders() });
}

export function apiUpload(path, formData) {
  return axios.post(`${API}${path}`, formData, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
  });
}

export function apiDownload(path) {
  return axios.get(`${API}${path}`, {
    headers: authHeaders(),
    responseType: 'blob',
  });
}

export { API_BASE };
